import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { initAnalytics } from "./lib/analytics";

// ── Sentry error tracking ─────────────────────────────────────────────────────
// Set VITE_SENTRY_DSN in .env.local / Vercel env vars.
// Leave it unset in dev to avoid noise.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,          // "production" | "development"
    release:     import.meta.env.VITE_APP_VERSION ?? "unknown",
    // Only sample 10% of transactions for performance — errors are always 100%
    tracesSampleRate: 0.1,
    // Suppress noisy browser extension / third-party errors
    ignoreErrors: [
      /ResizeObserver loop/i,
      /Non-Error promise rejection/i,
      /Loading chunk \d+ failed/i,
      /ChunkLoadError/i,
    ],
    beforeSend(event) {
      // Strip auth tokens from request headers just in case
      if (event.request?.headers) {
        delete event.request.headers["Authorization"];
      }
      return event;
    },
  });
}

// ── PostHog analytics ─────────────────────────────────────────────────────────
// Set VITE_POSTHOG_KEY in .env.local / Vercel env vars.
initAnalytics();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          minHeight: "100vh", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          background: "#0f1117", color: "#e2e4ea",
          fontFamily: "'Inter',sans-serif", textAlign: "center", padding: "24px",
        }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>⚠️</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
            Something went wrong
          </div>
          <div style={{ fontSize: 13, color: "#708898", marginBottom: 24, maxWidth: 400, lineHeight: 1.6 }}>
            An unexpected error occurred. Our team has been notified.
            {error instanceof Error && (
              <div style={{ marginTop: 12, fontFamily: "monospace", fontSize: 11,
                background: "#161b24", padding: "8px 12px", borderRadius: 6,
                border: "1px solid #2d3748", color: "#fc8181", wordBreak: "break-all" }}>
                {error.message}
              </div>
            )}
          </div>
          <button
            onClick={resetError}
            style={{
              background: "#4fd1c5", color: "#0f1117", border: "none",
              padding: "10px 24px", borderRadius: 6, cursor: "pointer",
              fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: 14,
            }}
          >
            Try again
          </button>
          <a href="/" style={{ marginTop: 16, color: "#708898", fontSize: 12,
            textDecoration: "underline", textUnderlineOffset: 3 }}>
            Return to home
          </a>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>
);
